import { useEffect, useState } from "react";
import { Tag, Plus, RefreshCw, Pencil, Trash2, Check, X, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import {
  loadContactGroupsFromFirestore,
  syncContactGroupsFromGoogle,
  createContactGroup,
  renameContactGroup,
  deleteContactGroup,
} from "../../utils/contactGroups";
import { useActionDialog } from "../ActionDialog";

export default function LabelsTab() {
  const [{ user }] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [actionId, setActionId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    const data = await loadContactGroupsFromFirestore(user?.id);
    setGroups(data);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleSyncFromGoogle = async () => {
    setSyncing(true);
    try {
      await syncContactGroupsFromGoogle(user.id);
      await refresh();
      toast.success("Labels synced from Google.");
    } catch (err) {
      console.error(err);
      toast.info(
        "Labels backend not deployed yet. Once Humza ships, sync starts working.",
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const created = await createContactGroup(user.id, name);
      setGroups((prev) =>
        [
          ...prev,
          {
            id: created.id,
            resourceName: created.resourceName,
            name: created.name,
            source: "user",
            memberCount: 0,
            ownerId: user.id,
          },
        ].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
      );
      setNewName("");
      toast.success("Label created.");
    } catch (err) {
      console.error(err);
      toast.info(
        "Labels backend not deployed yet. Your label will appear once Humza ships.",
      );
    } finally {
      setCreating(false);
    }
  };

  const startRename = (g) => {
    setEditingId(g.id);
    setEditDraft(g.name);
  };

  const handleRename = async (g) => {
    const name = editDraft.trim();
    if (!name || name === g.name) {
      setEditingId(null);
      return;
    }
    setActionId(g.id);
    try {
      await renameContactGroup(user.id, g.resourceName, name);
      setGroups((prev) =>
        prev.map((x) => (x.id === g.id ? { ...x, name } : x)),
      );
      setEditingId(null);
      toast.success("Renamed.");
    } catch (err) {
      console.error(err);
      toast.error("Rename failed (backend not deployed?).");
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (g) => {
    const ok = await confirm({
      title: "Delete Label",
      body: `Delete the label "${g.name}"?`,
      note: "Contacts in this label are not deleted, just the label itself.",
      tone: "danger",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setActionId(g.id);
    try {
      await deleteContactGroup(user.id, g.resourceName, false);
      setGroups((prev) => prev.filter((x) => x.id !== g.id));
      toast.success("Label deleted.");
    } catch (err) {
      console.error(err);
      toast.error("Delete failed (backend not deployed?).");
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
        <Tag size={18} className="text-linkColor" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900">
            Contact Labels
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Match Google Contacts' labels. Used for filtering and bulk
            sending templates. System labels (Friends, Family, etc.) come
            from Google.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSyncFromGoogle}
          disabled={syncing}
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 rounded-md px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
        >
          {syncing ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <RefreshCw size={13} />
          )}
          {syncing ? "Syncing…" : "Sync from Google"}
        </button>
      </div>

      <div className="px-5 py-4">
        <div className="flex items-center gap-2 mb-4">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New label name (e.g. Sphere of Influence)"
            className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCreate();
              }
            }}
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={!newName.trim() || creating}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md bg-themeColor hover:bg-themeColor/90 disabled:opacity-50 text-white"
          >
            <Plus size={13} />
            {creating ? "Creating…" : "Create label"}
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
        ) : groups.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm text-slate-500">
              No labels yet. Create one above, or click "Sync from Google"
              to import the ones you already have in Google Contacts.
            </p>
            <p className="text-[11px] text-slate-400 mt-2">
              Note: backend deployment is required before sync works.
            </p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {groups.map((g) => (
              <li
                key={g.id}
                className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md hover:bg-slate-50"
              >
                {editingId === g.id ? (
                  <>
                    <input
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRename(g);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      autoFocus
                      className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleRename(g)}
                      disabled={actionId === g.id}
                      className="size-7 rounded text-emerald-600 hover:bg-emerald-50 flex items-center justify-center disabled:opacity-50"
                      title="Save"
                    >
                      {actionId === g.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Check size={14} />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="size-7 rounded text-slate-500 hover:bg-slate-100 flex items-center justify-center"
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-slate-900">
                      {g.name}
                      {g.source === "system" && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                          Google built-in
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-slate-500">
                      {g.memberCount || 0} member
                      {(g.memberCount || 0) === 1 ? "" : "s"}
                    </span>
                    {g.source !== "system" && (
                      <>
                        <button
                          type="button"
                          onClick={() => startRename(g)}
                          className="size-7 rounded text-slate-500 hover:bg-slate-100 flex items-center justify-center"
                          title="Rename"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(g)}
                          disabled={actionId === g.id}
                          className="size-7 rounded text-red-500 hover:bg-red-50 flex items-center justify-center disabled:opacity-50"
                          title="Delete"
                        >
                          {actionId === g.id ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <Trash2 size={13} />
                          )}
                        </button>
                      </>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {dialog}
    </div>
  );
}
